"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useOverflow = void 0;
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
var react_1 = __importDefault(require("react"));
var useMergedRefs_1 = require("./useMergedRefs");
var useResizeObserver_1 = require("./useResizeObserver");
var STARTING_MAX_ITEMS_COUNT = 20;
/**
 * Hook that observes the size of an element and returns the number of items
 * that should be visible based on the size of the container element.
 *
 * The returned number should be used to render the element with fewer items.
 *
 * @private
 * @param items Items that this element contains.
 * @param disabled Set to true to disconnect the observer.
 * @param dimension 'horizontal' (default) or 'vertical'
 * @returns [callback ref to set on container, stateful count of visible items]
 *
 * @example
 * const items = Array(10).fill().map((_, i) => <span>Item {i}</span>);
 * const [ref, visibleCount] = useOverflow(items);
 * ...
 * return (
 *   <div ref={ref}>
 *     {items.slice(0, visibleCount)}
 *   </div>
 * );
 */
var useOverflow = function (items, disabled, orientation) {
    if (disabled === void 0) { disabled = false; }
    if (orientation === void 0) { orientation = 'horizontal'; }
    var containerRef = react_1.default.useRef(null);
    var _a = react_1.default.useState(function () {
        return disabled ? items.length : Math.min(items.length, STARTING_MAX_ITEMS_COUNT);
    }), visibleCount = _a[0], setVisibleCount = _a[1];
    var needsFullRerender = react_1.default.useRef(true);
    var _b = react_1.default.useState(0), containerSize = _b[0], setContainerSize = _b[1];
    var previousContainerSize = react_1.default.useRef(0);
    var updateContainerSize = react_1.default.useCallback(function (_a) {
        var width = _a.width, height = _a.height;
        return setContainerSize(orientation === 'horizontal' ? width : height);
    }, [orientation]);
    var _c = (0, useResizeObserver_1.useResizeObserver)(updateContainerSize), resizeRef = _c[0], observer = _c[1];
    var resizeObserverRef = react_1.default.useRef(observer);
    react_1.default.useLayoutEffect(function () {
        if (disabled) {
            setVisibleCount(items.length);
        }
        else {
            setVisibleCount(Math.min(items.length, STARTING_MAX_ITEMS_COUNT));
            needsFullRerender.current = true;
        }
    }, [containerSize, disabled, items]);
    var mergedRefs = (0, useMergedRefs_1.useMergedRefs)(containerRef, resizeRef);
    react_1.default.useLayoutEffect(function () {
        var _a;
        if (!containerRef.current || disabled) {
            (_a = resizeObserverRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
            return;
        }
        var dimension = orientation === 'horizontal' ? 'Width' : 'Height';
        var availableSize = containerRef.current["offset".concat(dimension)];
        var requiredSize = containerRef.current["scroll".concat(dimension)];
        if (availableSize < requiredSize) {
            var avgItemSize = requiredSize / visibleCount;
            var visibleItems = Math.floor(availableSize / avgItemSize);
            setVisibleCount(visibleItems);
        }
        else if (needsFullRerender.current) {
            var childrenSize = Array.from(containerRef.current.children).reduce(function (sum, child) { return sum + child["offset".concat(dimension)]; }, 0);
            var avgItemSize = childrenSize / visibleCount;
            var visibleItems = Math.floor(availableSize / avgItemSize);
            if (!isNaN(visibleItems)) {
                // Doubling the visible items to overflow the container. Just to be safe.
                setVisibleCount(Math.min(items.length, visibleItems * 2));
            }
        }
        needsFullRerender.current = false;
    }, [containerSize, visibleCount, disabled, items.length, orientation]);
    react_1.default.useLayoutEffect(function () {
        previousContainerSize.current = containerSize;
    }, [containerSize]);
    return [mergedRefs, visibleCount];
};
exports.useOverflow = useOverflow;
